import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import BankAccountService, { BankAccount } from '../services/BankAccountService';
import BankAccountForm from './BankAccountForm';

const BankAccounts: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [accounts, setAccounts] = React.useState<BankAccount[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');
  const [showForm, setShowForm] = React.useState(false);
  const [editingAccount, setEditingAccount] = React.useState<BankAccount | null>(null);
  const [prefillExample, setPrefillExample] = React.useState(false);

  const loadAccounts = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await BankAccountService.getBankAccounts();
      setAccounts(data);
    } catch (e: any) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message;
      if (status === 401) {
        setError('Sitzung abgelaufen – bitte neu anmelden.');
      } else {
        setError(`Fehler beim Laden der Bankkonten: ${detail || 'Unbekannter Fehler'}`);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const open = params.get('open');
    const prefill = params.get('prefill');

    if (open === 'create') {
      setShowForm(true);
      setPrefillExample(prefill === 'example');
    }
  }, [location.search]);

  const handleSetDefault = async (id: number) => {
    try {
      await BankAccountService.setAsDefault(id);
      setSuccess('Standard-Lohnkonto erfolgreich geändert');
      await loadAccounts();
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message;
      setError(`Fehler beim Setzen des Standard-Kontos: ${detail || 'Unbekannter Fehler'}`);
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`Bankkonto "${name}" wirklich löschen?`)) return;

    try {
      await BankAccountService.deleteBankAccount(id);
      setSuccess('Bankkonto erfolgreich gelöscht');
      await loadAccounts();
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message;
      setError(`Fehler beim Löschen: ${detail || 'Unbekannter Fehler'}`);
    }
  };

  const handleEdit = (account: BankAccount) => {
    setEditingAccount(account);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingAccount(null);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingAccount(null);
    setPrefillExample(false);
    loadAccounts();

    const params = new URLSearchParams(location.search);
    const returnTo = params.get('returnTo');
    if (returnTo) {
      const decodedReturnTo = decodeURIComponent(returnTo);
      const separator = decodedReturnTo.includes('?') ? '&' : '?';
      navigate(`${decodedReturnTo}${separator}bankAccountCreated=1`);
    }
  };

  const qp = new URLSearchParams(location.search);
  const from = qp.get('from');

  return (
    <div style={{ padding: 24, maxWidth: 1200 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, marginBottom: 8, color: '#0f2b5a' }}>Zahlkonten (Bankverbindungen)</h1>
          <p style={{ fontSize: 14, color: '#666', margin: 0 }}>
            Verwalten Sie die Bankkonten für SEPA-Lohnüberweisungen.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {from && (
            <button
              type="button"
              onClick={() => navigate(decodeURIComponent(from))}
              style={{
                padding: '10px 20px',
                background: '#fff',
                color: '#021a4a',
                border: '1px solid #021a4a',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600,
              }}
              aria-label="Zurück zu Payments"
            >
              ← Zurück zu Payments
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowForm(true)}
            style={{
              padding: '10px 20px',
              background: '#021a4a',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            + Bankkonto hinzufügen
          </button>
        </div>
      </div>

      {error && (
        <div
          style={{
            padding: 12,
            marginBottom: 16,
            background: '#ffebee',
            border: '1px solid #ef5350',
            borderRadius: 6,
            color: '#c62828',
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      {success && (
        <div
          style={{
            padding: 12,
            marginBottom: 16,
            background: '#e8f5e9',
            border: '1px solid #4caf50',
            borderRadius: 6,
            color: '#2e7d32',
            fontSize: 14,
          }}
        >
          {success}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>Lade Bankkonten...</div>
      ) : accounts.length === 0 ? (
        <div
          style={{
            padding: 40,
            textAlign: 'center',
            background: '#f8f9fa',
            border: '1px solid #e0e3ef',
            borderRadius: 8,
            color: '#666',
          }}
        >
          <p style={{ marginBottom: 16 }}>Noch keine Bankkonten vorhanden.</p>
          <button
            type="button"
            onClick={() => setShowForm(true)}
            style={{
              padding: '8px 16px',
              background: '#021a4a',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            Erstes Bankkonto hinzufügen
          </button>
        </div>
      ) : (
        <div
          style={{
            background: '#fff',
            border: '1px solid #e0e3ef',
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e0e3ef', background: '#f8f9fa' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: 13, color: '#666', fontWeight: 600 }}>
                  Name
                </th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: 13, color: '#666', fontWeight: 600 }}>
                  Kontoinhaber
                </th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: 13, color: '#666', fontWeight: 600 }}>
                  IBAN
                </th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: 13, color: '#666', fontWeight: 600 }}>
                  BIC
                </th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: 13, color: '#666', fontWeight: 600 }}>
                  Bank
                </th>
                <th style={{ padding: '12px', textAlign: 'center', fontSize: 13, color: '#666', fontWeight: 600 }}>
                  Status
                </th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: 13, color: '#666', fontWeight: 600 }}>
                  Aktionen
                </th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((account) => (
                <tr
                  key={account.id}
                  style={{
                    borderBottom: '1px solid #f0f0f0',
                    background: account.is_payroll_default ? '#f8f9fa' : '#fff',
                  }}
                >
                  <td style={{ padding: '16px 12px', fontSize: 14, fontWeight: account.is_payroll_default ? 600 : 400 }}>
                    {account.name}
                    {account.is_payroll_default && (
                      <span
                        style={{
                          marginLeft: 8,
                          padding: '2px 8px',
                          background: '#e8f5e9',
                          color: '#2e7d32',
                          borderRadius: 8,
                          fontSize: 11,
                          fontWeight: 600,
                        }}
                      >
                        Standard
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '16px 12px', fontSize: 14, color: '#495057' }}>{account.holder_name}</td>
                  <td style={{ padding: '16px 12px', fontSize: 14, fontFamily: 'monospace', color: '#495057' }}>
                    {account.iban_masked}
                  </td>
                  <td style={{ padding: '16px 12px', fontSize: 14, color: '#495057' }}>{account.bic || '—'}</td>
                  <td style={{ padding: '16px 12px', fontSize: 14, color: '#495057' }}>{account.bank_name || '—'}</td>
                  <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                    {account.active ? (
                      <span style={{ color: '#4caf50', fontSize: 12 }}>✓ Aktiv</span>
                    ) : (
                      <span style={{ color: '#999', fontSize: 12 }}>Archiviert</span>
                    )}
                  </td>
                  <td style={{ padding: '16px 12px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      {!account.is_payroll_default && (
                        <button
                          type="button"
                          onClick={() => handleSetDefault(account.id)}
                          style={{
                            padding: '6px 12px',
                            background: '#fff',
                            color: '#021a4a',
                            border: '1px solid #021a4a',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: 13,
                          }}
                        >
                          Als Standard
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleEdit(account)}
                        style={{
                          padding: '6px 12px',
                          background: '#fff',
                          color: '#666',
                          border: '1px solid #d0d4e4',
                          borderRadius: 4,
                          cursor: 'pointer',
                          fontSize: 13,
                        }}
                      >
                        Bearbeiten
                      </button>
                      {!account.is_payroll_default && (
                        <button
                          type="button"
                          onClick={() => handleDelete(account.id, account.name)}
                          style={{
                            padding: '6px 12px',
                            background: '#fff',
                            color: '#ef5350',
                            border: '1px solid #ef5350',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: 13,
                          }}
                        >
                          Löschen
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={handleFormClose}
        >
          <div
            style={{
              background: '#fff',
              padding: 24,
              borderRadius: 8,
              maxWidth: 600,
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0, marginBottom: 16, fontSize: 20, color: '#0f2b5a' }}>
              {editingAccount ? 'Bankkonto bearbeiten' : 'Neues Bankkonto hinzufügen'}
            </h2>
            <BankAccountForm
              account={editingAccount}
              prefillExample={prefillExample}
              onSuccess={handleFormSuccess}
              onCancel={handleFormClose}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BankAccounts;
